<template>
  <v-container grid-list-md text-xs-center>
    <v-layout row wrap>
      <v-flex xs12>
        <v-card class="chart-card">
          <ChartJSLine
            v-if="loaded"
            :styles="chartStyle"
            :chartData="chartData"
            :chartOptions="chartOptions"
          />
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import ChartJSLine from './ChartJSLine'
export default {
  name: 'DeviceMessagesGraph',
  components: { ChartJSLine },
  props: {
    title: { type: String, required: true },
    dateFrom: { type: String, required: true, default: '' },
    dateTo: { type: String, required: true, default: '' },
    deviceID: { type: String, required: true },
    deviceType: { type: String, required: true }
  },
  data: () => ({
    loaded: false,
    chartData: null,
    chartOptions: null,
    chartStyle: {
      position: 'relative',
      width: '100%'
    },
    deviceMessages: [],
    // From OrgSettings.json
    deltakeys: [],
    dataformats: [],
    computedText: 'Actuals',
    isyAxis1: false
  }),
  computed: {
    ...mapGetters({ vueFrontendConfig: 'vueFrontendConfig' })
  },
  methods: {
    loadOrganisationJsonData () {
      if (this.vueFrontendConfig !== undefined) {
        try {
          var graphplots = this.vueFrontendConfig.graphplots
          this.deltakeys = graphplots.deltakeys !== undefined ? graphplots.deltakeys : []
          this.dataformats = graphplots.dataformats !== undefined ? graphplots.dataformats : []
        } catch (err) {
          var errmsg = `Error(${err} - reading OrgSettings.json)`
          // eslint-disable-next-line
          console.log(errmsg)
          this.$emit('error', errmsg)
        }
      }
    },
    requestFailed (error) {
      this.$emit('error', error)
    },
    addAnnotations (lines) {
      if (lines && lines.length > 0) {
        var annotations = []
        for (var i = 0; i < lines.length; i++) {
          annotations[i] = {
            id: 'line' + i,
            type: 'line',
            mode: 'horizontal',
            scaleID: 'y-axis-0',
            value: lines[i].value,
            // borderDash: [2, 4],
            borderColor: 'black',
            borderWidth: 2,
            label: {
              backgroundColor: lines[i].colour,
              content: lines[i].text,
              enabled: lines[i].enabled
            }
          }
          var annotation = {
            drawTime: 'afterDatasetsDraw',
            events: ['click'],
            annotations
          }
        }
        // prettier-ignore
        this.chartOptions =
          Object.assign(
            {},
            this.chartOptions,
            { annotation }
          )
      }
    },
    displayDeviceMessagesGraph (response) {
      if (response.data.messages && response.data.messages.length > 0) {
        // Create the chartdata - message values, device specific graphs and metrics.deltas
        this.chartData = this.createChartData(response.data.messages,
          response.data.graph_format,
          response.data.metrics)
        // Set the options and annotations
        this.chartOptions = {
          title: {
            display: true,
            text: `${this.title} - ${this.computedText}`
          },
          maintainAspectRatio: false,
          spanGaps: true,
          scales: {
            xAxes: [{
              position: 'bottom',
              id: 'x-axis-0',
              display: true
            }, {
              position: 'top',
              id: 'x-axis-1',
              display: false
            }],
            yAxes: [{
              position: 'left',
              id: 'y-axis-0',
              display: true
            }, {
              position: 'right',
              id: 'y-axis-1',
              display: this.isyAxis1
            }]
          }
        }
        this.addAnnotations(response.data.graph_format.AnnotationsLines)
        // Now we have 'loaded' the data.
        this.loaded = true
      } else {
        this.requestFailed('No device messages returned')
      }
    },
    createChartData (messages, graphFormat, metrics) {
      var labels = []
      var keys = []
      var datasets = []
      var deltakeys = []
      var showDeltaRate = false
      //
      // Read the deltakeys from the config json for the - devicetype.['keynames'..]
      //
      var objDeltaKeys = this.deltakeys.find(({ devicetype }) => devicetype === this.deviceType)
      // DEBUG - uncomment to see
      // eslint-disable-next-line
      //console.log(objDeltaKeys)
      if (objDeltaKeys !== undefined && objDeltaKeys.showDeltas) {
        deltakeys = objDeltaKeys.keynames
        if (objDeltaKeys.showRate !== undefined && objDeltaKeys.showRate) {
          showDeltaRate = true
          this.computedText = 'Computed Rate/s'
        } else {
          this.computedText = 'Computed Deltas'
        }
      }
      //
      // Read the formatkeys to be plotted - apply ovverides from the config json for the - devicetype.['datakeys'..]
      //
      var isyAxis1 = false
      var objDataFormats = this.dataformats.find(({ devicetype }) => devicetype === this.deviceType)
      var doOverrides = objDataFormats !== undefined
      var graphDataFormatsToPlot = []
      // Parse the graph_format dataset Formats: (keys, colours, ..)  passed in with the message response data
      graphFormat.DataFormats.forEach(function (deviceObj, i) {
        // override graphformat from dataset configuration - as defined in org settings json
        if (doOverrides) {
          objDataFormats.datakeys.forEach(function (configObj) {
            if (configObj.key === deviceObj.key) {
              deviceObj.label = configObj.label
              graphDataFormatsToPlot.push(deviceObj)
              keys.push(deviceObj.key)
              datasets.push([])
            }
          })
        } else {
          graphDataFormatsToPlot.push(deviceObj)
          keys.push(deviceObj.key)
          datasets.push([])
        }
        if (deviceObj.yAxisID !== undefined && deviceObj.yAxisID === 'y-axis-1') {
          isyAxis1 = true
          // DEBUG - uncomment to see
          // eslint-disable-next-line
          //console.log(deviceObj.yAxisID)
        }
      })
      this.isyAxis1 = isyAxis1
      // DEBUG - uncomment to see
      // eslint-disable-next-line
      //console.log(graphDataFormatsToPlot)
      messages.forEach(function (msg) {
        var kk = []
        // FILTER messages, remove non chart data values
        // For all device types, decoded by the codec class - we only want 'Standard Report'
        // NOTE: backend API 'promise' should always set msgType=1 for data values to be plotted
        if (msg.decoded_data && (msg.decoded_data.msgType === 1 || msg.decoded_data.msgType === 2)) {
          // var mset = msg.decoded_data.msgType - 1
          keys.forEach(function (k, i) {
            kk[i] = k
            // If 'key' is a multi-variable readings value # [1..N]
            if (k.indexOf('#') > 0) {
              // Get the 'key' field and strip the # number placeholder, timedelta and units
              // Example: If a pressure device expect 'key' = 'PRESSURE#15#minutes' and number of readings = 12
              var readingskey = k.split('#')

              // Extrapolate / cast the multiple readings into their individual corrected timestamp deltas'
              // ..from 0 reading to penultimate reading (# 10)
              for (var n = 0; n < msg.decoded_data.readings - 1; n++) {
                // Get the 'key#' name value (1 based)
                var keyname = `${readingskey[0]}${(n + 1)}`

                // Compute the timedelta = xtime (multiplier) * (no of minutes)
                var xtime = msg.decoded_data.xtime !== undefined ? msg.decoded_data.xtime : 1
                var timedelta = (msg.decoded_data.readings - n - 1) * xtime * readingskey[1]

                // DEBUG - uncomment to see inflated readings in the console
                // eslint-disable-next-line
                //console.log('Reading(' + keyname + ') @' + msg.timestamp + '[-' + timedelta + '] = ' + msg.decoded_data[keyname])
                datasets[i].push(msg.decoded_data[keyname])
                // push the time only for the first  key (since all dataset values are aligned to the same time delta)
                if (i === 0) labels.push(moment(msg.timestamp).subtract(timedelta, readingskey[2]).format('DD/MM/YYYY HH:mm'))
              }
              // The final last dataset[] reading 'key#' name (# 11)
              kk[i] = `${readingskey[0]}${msg.decoded_data.readings}`
            }
            var value = msg.decoded_data[kk[i]]
            // if config specifies to use delta then find matching delta value from metrics set
            if (deltakeys.includes(k)) {
              var objDeltaKeyValue = metrics.deltas[k].find(({ timestamp }) => timestamp === msg.timestamp)
              // DEBUG - uncomment to see inflated readings in the console
              // eslint-disable-next-line
              //console.log(objDeltaKeyValue)
              if (objDeltaKeyValue !== undefined) {
                if (showDeltaRate) {
                  value = objDeltaKeyValue.rate
                } else {
                  value = objDeltaKeyValue.value
                }
              } else {
                // Did not find value with matching timestamp so set to average
                if (showDeltaRate) {
                  value = 0
                } else {
                  value = metrics.deltas[`${k}_avg`]
                }
              }
            }
            datasets[i].push(value)
            // DEBUG - uncomment to see inflated readings in the console
            // eslint-disable-next-line
            //console.log('Reading(' + kk[i] + ') @' + msg.timestamp + '[-0] = ' + msg.decoded_data[kk[i]])
          })
          labels.push(moment(msg.timestamp).format('DD/MM/YYYY HH:mm'))
        }
      })
      graphDataFormatsToPlot.forEach(function (obj, i) {
        obj.data = datasets[i]
        delete obj.key
      })
      // Return chart data.
      return {
        labels: labels,
        datasets: graphDataFormatsToPlot
      }
    }
  },
  beforeMount () {
    this.loadOrganisationJsonData()
    this.loaded = false

    var url = `/schemas/devicename_messages/${this.deviceID}`
    if (this.dateFrom.length > 0) {
      url = url + `?datefrom=${this.dateFrom}`
      if (this.dateTo.length > 0) {
        url = url + `&dateto=${this.dateTo}`
      }
    }
    // Make the API request to get the messages for this device.
    // Response data includes:
    // - messages[]
    // - graph_format[] specifying dataset 'keys' and colour schemes to be plotted
    this.$http
      .get(url)
      .then(response => this.displayDeviceMessagesGraph(response))
      .catch(error => this.requestFailed(error))
  }
}
</script>

<style>
.chart-card {
  padding: 30px;
}
</style>
